import React from 'react'
import cx from 'classnames'
import { Button, Heading } from '../../../../views/components'
import { INVOICE_PREFIX } from '../../../api/accounting/constants'
import styles from './NextInvoiceNumber.module.scss'

type TProps = {
  /** MAX(last_billing_invoice_number). Null while the settings load. */
  lastUsed: number | null
  /** How many distinct values the table holds. Anything but 1 is a fault. */
  distinctValues: number | null
  loading?: boolean
  onEdit: () => void
}

// Big type is the NEXT number, not the stored counter — naming only the
// stored value is what caused the off-by-one this screen exists to end.
const NextInvoiceNumber = ({ lastUsed, distinctValues, loading = false, onEdit }: TProps) => {
  const hasValue = lastUsed !== null
  const next = hasValue ? lastUsed + 1 : null
  const isSplit = distinctValues !== null && distinctValues > 1

  return (
    <section className={cx(styles.root, { [styles['is-loading']]: loading })} aria-busy={loading}>
      <div className={styles.main}>
        <p className={styles.eyebrow}>Next invoice number</p>
        <Heading as="p" className={styles.number}>
          {hasValue ? (
            <>
              <span className={styles.prefix}>{INVOICE_PREFIX}</span>
              <span className={styles.digits}>{next}</span>
            </>
          ) : (
            <span className={styles.placeholder}>—</span>
          )}
        </Heading>
        <p className={styles.caption}>
          {hasValue ? (
            <>
              Last used{' '}
              <strong className={styles['caption-strong']}>
                {INVOICE_PREFIX}
                {lastUsed}
              </strong>
            </>
          ) : (
            'Reading the current counter…'
          )}
        </p>
      </div>

      <div className={styles.actions}>
        {/* contrast is the design system's variant for a dark ground. */}
        <Button contrast className={styles.action} onClick={onEdit} disabled={!hasValue}>
          Set invoice number
        </Button>
      </div>

      {isSplit && (
        <p className={styles.warning} role="alert">
          <strong>The counter is split.</strong> {distinctValues} different values are stored across the agency rows.
          The billing run takes the highest, so everything below it is skipped rather than issued. Setting a number here
          writes one value to every row.
        </p>
      )}
    </section>
  )
}

export default NextInvoiceNumber
